import React from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Column, Carousel, List } from '@pella/sash-framework';
import { CTA } from '../../';
import { isValidArray, objectHasValues } from '../../utilities/validations';

import AnchorLinks from '../anchor-links';
import CardRenderer from '../card-types/card-renderer';

import styles from './index.module.scss';

const CardSection = ({ data, className, headingTag = 'h2', pdpUrl, carouselSettings = {}, ...other }) => {
	if (!data) return null;

	const slides = data.slides ? JSON.parse(data.slides) : { xs: 12, md: 6, xl: 4 };
	const CustomHeading = headingTag;
	// TODO: Remove when transition to data.card is complete in Contentstack
	const cards = isValidArray(data.card) && objectHasValues(data.card[0]) ? data.card : data.cards;

	return (
		<div
			className={`cardSection py-8 md:py-12 ${data.color_background ? 'bg-neutral-50' : ''} ${data.classname || ''} ${className || ''}`}
			data-testid='card-section'
			{...other}
		>
			<Container className={data.layout === 'aside' ? `${styles.cardsAside} grid grid-cols-1 items-center gap-4 md:gap-8 lg:grid-cols-[1fr_2fr]` : null}>
				{(data.heading || data.description || isValidArray(data.cta)) && (
					<div>
						{data.heading && (
							<CustomHeading
								dangerouslySetInnerHTML={{ __html: data.heading }}
								className={`mb-2 ${data.layout !== 'aside' ? 'text-center' : 'max-lg:text-center'}`}
							/>
						)}
						{data.description && (
							<div
								dangerouslySetInnerHTML={{ __html: data.description }}
								className={`text-subtitle-2 mb-4 ${data.layout !== 'aside' ? 'mx-auto max-w-[80ch] text-center' : 'max-lg:text-center'}`}
							/>
						)}
						{data.layout === 'award' && isValidArray(data.cta) && (
							<AnchorLinks
								data={data.cta}
								className={`mb-0 mt-4 md:mt-6`}
							/>
						)}
					</div>
				)}

				<div className={data.layout === 'above' ? 'mt-6 md:mt-8' : null}>
					{!data.carousel && isValidArray(cards) && (
						<Row
							justify='center'
							verticalGutters
						>
							{cards.map((card, index) => (
								<Column
									key={index}
									{...slides}
								>
									<CardRenderer
										data={card}
										pdpUrl={pdpUrl}
									/>
								</Column>
							))}
						</Row>
					)}
					{data.carousel && isValidArray(cards) && (
						<Carousel
							slidesToShow={slides}
							slidePadding={20}
							arrowsClassName='max-md:hidden'
							showArrows
							showDots
							{...carouselSettings}
						>
							{cards.map((card, index) => (
								<CardRenderer
									key={index}
									data={card}
									pdpUrl={pdpUrl}
								/>
							))}
						</Carousel>
					)}
				</div>

				{data.layout !== 'award' && isValidArray(data.cta) && (
					<List
						bullets='none'
						className='mt-5 flex justify-center md:mt-7 lg:col-span-2'
					>
						{data.cta.map((cta, index) => (
							<li
								key={index}
								className='my-1 md:mx-2'
							>
								<CTA data={cta} />
							</li>
						))}
					</List>
				)}
			</Container>
		</div>
	);
};

CardSection.propTypes = {
	data: PropTypes.object,
	className: PropTypes.string,
	headingTag: PropTypes.string,
	pdpUrl: PropTypes.string,
	carouselSettings: PropTypes.object,
};

export default CardSection;
